import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import WeatherLoader from "../../components/loader";
import store from "../../store";
import "./styles/_index.scss";
import TableRow from "@mui/material/TableRow";
import axios from "axios";
import { Button, CircularProgress } from "@mui/material";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

const drawerWidth = 0;
const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })<{
  open?: boolean;
}>(({ theme, open }) => ({
  height: "82vh",
  display: "flex",
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginLeft: `-${drawerWidth}px`,
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
}));

function calculateWindDir(data: number): string {
  data = data % 360;
  if (data < 0) data += 360;
  const directions = [
    "N",
    "NNE",
    "NE",
    "ENE",
    "E",
    "ESE",
    "SE",
    "SSE",
    "S",
    "SSW",
    "SW",
    "WSW",
    "W",
    "WNW",
    "NW",
    "NNW",
  ];
  let closestIndex = Math.round(data / 22.5) % 16;
  return directions[closestIndex];
}

type CriteriaDetail = {
  forecast_osf_criteria_id: number;
  field_id: number;
  field_name: string;
  margin_value: string;
  value: string;
  logical_operator_id: number | null;
};

type DataRecord = Record<string, number | null | undefined>;

const hBlue: React.CSSProperties = {
  background: "#ADD8E6",
  color: "#192b3c",
  textAlign: "center",
  padding: "2px",
   width: "100%",      
  display: "block",
};
const hBlueL: React.CSSProperties = {
  ...hBlue,
  borderLeft: "0.5px solid #437c92",
};
const hBlueG: React.CSSProperties = {
  ...hBlue,
  borderLeft: "0.5px solid gray",
};
const hWhite: React.CSSProperties = {
  background: "white",
  color: "#192b3c",
  textAlign: "center",
  padding: "2px 4px",
   width: "100%",        
  display: "block",
};

function TableValue(props: any) {
  const windcolumn = [
    "a_10mgust",
    "a_10mwindspeed",
    "a_50mwindspeed",
    "a_80mwindspeed",
    "a_100mwindspeed",
    "a_10mwinddir",
  ];
  const wavecolumn = ["windseadirection", "windseaheight", "windseaperiod"];
  const swell1coln = ["swell1height", "swell1direction", "swell1period"];
  const swell2coln = ["swell2height", "swell2direction", "swell2period"];
  const totalcoln = ["sigwaveheight", "maxwave", "peakwavedir", "totalseadir_virtual", "peakwaveperiod"];
  const currentcoln = ["surfacecurrentdirection", "surfacecurrentspeed"];

  const inputRef = useRef<any>(null);
  const [localInputText, setLocalInputText] = useState(
    props.inputText[props.name] ?? "",
  );
  const [localSelect, setLocalSelect] = useState(
    props.selectText[props.name] ?? ">=",
  );

  useEffect(() => {
    if (props.name === props.inputRefValue) inputRef.current?.focus();
  }, [props.inputRefValue, props.name]);

  const handleSymbol = (event: any) => {
    const newSymbol = event.target.value;
    let select = { ...props.selectText };
    if (windcolumn.includes(props.name))
      windcolumn.forEach((el) => {
        select[el] = newSymbol;
      });
    else if (wavecolumn.includes(props.name))
      wavecolumn.forEach((el) => {
        select[el] = newSymbol;
      });
    else if (swell1coln.includes(props.name))
      swell1coln.forEach((el) => {
        select[el] = newSymbol;
      });
    else if (swell2coln.includes(props.name))
      swell2coln.forEach((el) => {
        select[el] = newSymbol;
      });
    else if (totalcoln.includes(props.name))
      totalcoln.forEach((el) => {
        select[el] = newSymbol;
      });
    else if (currentcoln.includes(props.name))
      currentcoln.forEach((el) => {
        select[el] = newSymbol;
      });
    props.setSelectText(select);
    if (localSelect !== newSymbol) {
      setLocalSelect(newSymbol);
      props.updateAgain(props.name, newSymbol);
    }
  };

  const handleInputChange = (event: any) => {
    props.setInputText((prev: any) => ({
      ...prev,
      [props.name]: event.target.value,
    }));
    setLocalInputText(event.target.value);
    props.setInputRefValue(props.name);
    props.analysetheData(props.name, event.target.value, localSelect);
    props.setRender((prev: any) => !prev);
  };

  const isDirectionField =
    props.name === "a_10mwinddir" ||
    props.name === "windseadirection" ||
    props.name === "swell1direction" ||
    props.name === "swell2direction" ||
    props.name === "surfacecurrentdirection" ||
    props.name === "peakwavedir" ||
    props.name === "totalseadir_virtual";

  const shouldHideControl = () => {
    const weatherFields = [
      "modelvisibility",
      "cloudbase",
      "rainrate",
      "2mtemp",
      "vis",
      "amt",
      "mslp",
    ];
    if (
      weatherFields.some((f) =>
        props.name.toLowerCase().includes(f.toLowerCase()),
      )
    )
      return false;
    if (windcolumn.includes(props.name) && !props.wind) return true;
    if (wavecolumn.includes(props.name) && !props.windwave) return true;
    if (swell1coln.includes(props.name) && !props.swell1) return true;
    if (swell2coln.includes(props.name) && !props.swell2) return true;
    if (totalcoln.includes(props.name) && !props.total) return true;
    if (currentcoln.includes(props.name) && !props.currents) return true;
    return false;
  };

  const isWeatherField = () =>
    !windcolumn.includes(props.name) &&
    !wavecolumn.includes(props.name) &&
    !swell1coln.includes(props.name) &&
    !swell2coln.includes(props.name) &&
    !totalcoln.includes(props.name) &&
    !currentcoln.includes(props.name) &&
    props.name !== "datetimeutc";

  return (
    <div className={isDirectionField ? "rangediv" : "tableTitlediv"}>
      {props.name !== "datetimeutc" && !isWeatherField() ? (
        <div
          className={isDirectionField ? "selection" : "tableButtons"}
          style={{
            opacity: props.caption === "10m Wind Dir" ? 0 : 1,
            display: shouldHideControl() ? "none" : "block",
          }}
        >
          {isDirectionField ? (
            <select value={localSelect} onChange={handleSymbol}>
              <option value="<=">{"<="}</option>
              <option value=">=">{">="}</option>
            </select>
          ) : (
            <input
              value={localInputText}
              type="number"
              autoFocus={props.name === props.inputRefValue}
              onChange={handleInputChange}
              ref={inputRef}
            />
          )}
        </div>
      ) : props.name === "datetimeutc" ? (
        <>LIMIT</>
      ) : (
        <></>
      )}
    </div>
  );
}

function TableUnit(props: any) {
  const map: Record<string, string> = {
    Dir: "[°]",
    Ws10m: "[kn]",
    Wg10m: "[kn]",
    Wg50m: "[kn]",
    Ws50m: "[kn]",
    Ws100m: "[kn]",
    "2m Temp": "[c]",
    Amt: "[mm]",
    MSLP: "[mbar]",
    "Cloud Base": "[m]",
    Hs: "[m]",
    Ts: "[s]",
    "Peak Wave Period": "[s]",
    "Peak Wave Dir": "[cardinals]",
    Hmax: "[m]",
    Tp: "[s]",
    Vis: "[km]",
    "Rain Rate": "[mm/hr]",
    CB: "[ft]",
    Prec: "[mm/hr]",
    Sp: "[kts]",
    UTC: "date",
    "2m RH": "[%]",
    "Heat Index": "[C]",
    "Total Cloud": "[%]",
    Wx: "",
  };
  const text = map[props.caption] ?? props.caption;
  return (
    <b>
      <span className="tableTitle">{text === "date" ? <br /> : text}</span>
    </b>
  );
}

function TableTitle(props: any) {
  return (
    <>
      <span className="tableTitle">{props.caption}</span>
      <span className="tableSubTitle">{props.subCaption}</span>
    </>
  );
}

const ForeCast = () => {
  const windowWidth = useRef(window.innerWidth);
  const incrRef = useRef(0);

  const [latLand, setLatLang] = useState(false);
  const [tableColorDatas, setTableColorDatas] = useState<any | object>({});
  const [tableHeader, setTableHeaders] = useState<any>([]);
  const [forecastDatas, setForecastDatas] = useState<any>([]);
  const [TableDatas, setTableDatas] = useState<any>([]);
  const [page, setPage] = useState(0);
  const [selectText, setSelectText] = useState<object | any>({});
  const [inputText, setInputText] = useState<object | any>({});
  const [interval, setInterval] = useState<number>(3);
  const [open, setOpen] = useState(windowWidth.current > 1000 ? true : false);
  const [discussion, setDiscussion] = useState<any>("");
  const [disDetail, setDisDetail] = useState<any>("");
  const modelvisibility: any = [];
  const [min, setMin] = useState<any>(0);
  const [max, setMax] = useState<any>(0);
  const [overViewColor, setOverViewColor] = useState<any>([]);
  const [criteriaDatas, setCriteriaDatas] = useState<any>([]);
  const [criteriaDetailDatas, setCriteriaDetailDatas] = useState<any>([
    "white",
  ]);
  const [showWxWindow, setShowWxWindow] = useState(false);
  const [SelectValue, setSelectValue] = useState<any>("");
  const data = useSelector((state: any) => state?.app);
  const [wind, setWind] = useState(0);
  const [windwave, setWindWave] = useState(0);
  const [swell1, setSwell1] = useState(0);
  const [swell2, setSwell2] = useState(0);
  const [total, setTotal] = useState(0);
  const [weather, setWeather] = useState(0);
  const [timestraping, setTimeStraping] = useState([]);
  const [isDirTS, setIsDirTs] = useState<boolean>(false);
  const [isDirWw, setIsDirWw] = useState<boolean>(false);
  const [presentWw, setPreWw] = useState<boolean>(false);
  const [presentS1, setPreS1] = useState<boolean>(false);
  const [presentS2, setPreS2] = useState<boolean>(false);
  const [render, setRender] = useState(false);
  const [tableContent, setTableContent] = useState<any>();
  const [inputRefValue, setInputRefValue] = useState<any>();
  const [height, setHeight] = useState("55vh");
  const [subject, setSubject] = useState<any>("");
  const [validity_a, setValidity_a] = useState<any>("");
  const [validity_b, setValidity_b] = useState<any>("");
  const [loadingCsv, setLoadingCsv] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [currents, setCurrents] = useState(0);
  const [currentS3, setCurrentS3] = useState<boolean>(false);
  const [noDataMessage, setNoDataMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [maxMode, setMaxMode] = useState<"ALL" | 3 | 6 | 12 | 24>("ALL");

  useEffect(() => {
    const isDesktop = window.matchMedia("(min-width: 1024px)").matches;
    if (isDesktop) {
      setHeight("65vh");
    }
  }, []);

  const handleDownload = async () => {
    const projectName = localStorage.getItem("project");
    if (projectName) {
      const encodedProjectName = encodeURIComponent(projectName.trim());
      setLoadingPdf(true);
      fetch(
        `${process.env.REACT_APP_BACKEND_IP}/api/download-pdf/?projectName=${encodedProjectName}`,
      )
        .then((response) => {
          if (!response.ok) {
            throw new Error(
              `No matching PDF is available for the ${projectName}.`,
            );
          }
          return response.blob();
        })
        .then((blob) => {
          const url = window.URL.createObjectURL(new Blob([blob]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `${projectName}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.parentNode?.removeChild(link);
        })
        .catch((error) => {
          alert(error.message);
        })
        .finally(() => {
          setLoadingPdf(false);
        });
    }
  };

  const handleDownloadCsv = async () => {
    const projectName = localStorage.getItem("project");
    if (projectName) {
      const encodedProjectName = encodeURIComponent(projectName.trim());
      setLoadingCsv(true);
      fetch(
        `${process.env.REACT_APP_BACKEND_IP}/api/download_csv/?projectName=${encodedProjectName}`,
      )
        .then((response) => {
          if (!response.ok) {
            throw new Error("No matching CSV found");
          }
          return response.blob();
        })
        .then((blob) => {
          const url = window.URL.createObjectURL(new Blob([blob]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `${projectName}.csv`);
          document.body.appendChild(link);
          link.click();
          link.parentNode?.removeChild(link);
        })
        .catch((error) => {
          alert(error.message);
        })
        .finally(() => {
          setLoadingCsv(false);
        });
    }
  };

  function getCompassDirection(value: number): string {
    const directions = [
      "N",
      "NNE",
      "NE",
      "ENE",
      "E",
      "ESE",
      "SE",
      "SSE",
      "S",
      "SSW",
      "SW",
      "WSW",
      "W",
      "WNW",
      "NW",
      "NNW",
    ];
    const index = Math.round(value / 22.5) % 16;
    return directions[index];
  }

  const dayNames = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  useEffect(() => {
    store.dispatch({
      type: "TOGGLE_MENU",
      payload: windowWidth.current > 1000 ? true : false,
    });
  }, []);

  useEffect(() => {
    setOpen(data.toggle);
  }, [data]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_IP}/api/overview/`,
          { forecast_id: localStorage.getItem("fid") },
          {
            headers: {
              "Content-type": "application/json; charset=UTF-8",
              Authorization: "Basic " + btoa("admin:admin"),
            },
          },
        );
        const data = response.data;
        setCriteriaDatas(data.criteria_datas);
        setCriteriaDetailDatas(data.criteria_detail_datas);
        setShowWxWindow(
          !(data.criteria_datas && data.criteria_datas.length === 0) &&
            !(
              data.criteria_detail_datas &&
              data.criteria_detail_datas.length === 0
            ),
        );
        setSelectValue(
          String(data.criteria_detail_datas[0].forecast_osf_criteria_id),
        );
      } catch (error) {
        setCriteriaDatas([]);
        setCriteriaDetailDatas([]);
        setShowWxWindow(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_IP}/api/forecast/`,
          { forecast_id: localStorage.getItem("fid"), length: 0 },
          { headers: { "Content-type": "application/json; charset=UTF-8" } },
        );
        const data = response.data;
        if (!data || !data.headers || data.headers.length === 0) {
          setNoDataMessage("No forecast data available");
          setLoading(false);
          return;
        }
        setTableHeaders(data.headers);
        setForecastDatas(data.datas);
        setTableDatas(data.datas);
        setInterval(data.interval);
        setDiscussion(data.discussion.discussion);
        setDisDetail(data.discussion.discussion_detail);
        setSubject(data.discussion.subject);
        setValidity_a(data.discussion.validity_a);
        setValidity_b(data.discussion.validity_b);
        setLoading(false);

        const validityB = data.discussion.validity_b;
        if (validityB?.latitude && validityB?.longitude) {
          localStorage.setItem("lat", String(validityB.latitude));
          localStorage.setItem("lon", String(validityB.longitude));
        }

        const wind =
          data.subheaders.find((sub: any) => sub.hasOwnProperty("wind"))
            ?.wind || 0;
        const windWave =
          data.subheaders.find((sub: any) => sub.hasOwnProperty("windwave"))
            ?.windwave || 0;
        const swell1 =
          data.subheaders.find((sub: any) => sub.hasOwnProperty("swell1"))
            ?.swell1 || 0;
        const swell2 =
          data.subheaders.find((sub: any) => sub.hasOwnProperty("swell2"))
            ?.swell2 || 0;
        const total =
          data.subheaders.find((sub: any) => sub.hasOwnProperty("total"))
            ?.total || 0;
        const weather =
          data.subheaders.find((sub: any) => sub.hasOwnProperty("weather"))
            ?.weather || 0;
        const currents =
          data.subheaders.find((sub: any) => sub.hasOwnProperty("currents"))
            ?.currents || 0;
        setWind(wind);
        setWindWave(windWave);
        setSwell1(swell1);
        setSwell2(swell2);
        setTotal(total);
        setWeather(weather);
        setCurrents(currents);
      } catch (error) {
        setCriteriaDatas([]);
        setCriteriaDetailDatas([]);
        setShowWxWindow(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!forecastDatas || forecastDatas.length === 0) return;
    if (maxMode === "ALL") {
      setTableDatas(forecastDatas);
      setPage(0);
      return;
    }
    const fetchMaxHourly = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_IP}/api/forecast/max/${localStorage.getItem("fid")}`,
        );
        const hourlyMaxSource = response.data.datas;
        const merged = mergeMaxWithOriginal(
          forecastDatas,
          hourlyMaxSource,
          Number(maxMode),
        );
        setTableDatas(merged);
        setPage(0);
        setTableColorDatas({});
      } catch (err) {
        console.error("Error fetching hourly max source", err);
        setNoDataMessage("Error fetching max period data");
        setLoading(false);
      }
    };
    fetchMaxHourly();
  }, [maxMode, forecastDatas]);

  useEffect(() => {
    if (!TableDatas || TableDatas.length === 0) return;
    const timer = setTimeout(() => {
      const activeColumns = Object.keys(inputText).filter(
        (col) => inputText[col] && inputText[col].trim() !== "",
      );
      if (activeColumns.length === 0) return;
      let combinedTable: any = {};
      activeColumns.forEach((columnName) => {
        const text = inputText[columnName];
        const mode = selectText[columnName] || ">=";
        const inputValue = parseFloat(text);
        let total_index = 0;
        TableDatas.forEach((datas: any) => {
          for (const eachinner in datas) {
            let color = "white";
            const dataValue = getDataValue(
              datas[eachinner][columnName],
              columnName,
            );
            if (!isNaN(dataValue)) {
              if (
                (mode === ">=" && dataValue >= inputValue) ||
                (mode === "<=" && dataValue <= inputValue)
              )
                color = mode === ">=" ? "red" : "green";
            }
            combinedTable["color_" + columnName + "_" + total_index] = color;
            total_index++;
          }
        });
      });
      setTableColorDatas(combinedTable);
      const newColorArray: any = [];
      TableDatas.forEach((date: any) => {
        date.forEach((element: any) => {
          newColorArray.push(
            getColor(criteriaDetailDatas, element, SelectValue),
          );
        });
      });
      setOverViewColor(newColorArray);
    }, 100);
    return () => clearTimeout(timer);
  }, [TableDatas]);

  useEffect(() => {
    const newColorArray: any = [];
    TableDatas.forEach((date: any) => {
      date.forEach((element: any) => {
        let color = getColor(criteriaDetailDatas, element, SelectValue);
        newColorArray.push(color);
      });
    });
    setOverViewColor(newColorArray);
  }, [TableDatas, SelectValue, criteriaDetailDatas]);

  function mergeMaxWithOriginal(
    original: any[][],
    maxData: any[][],
    maxHours: number,
  ): any[][] {
    if (!original || !maxData || maxHours === 0) return original;
    const MS_PER_HOUR = 60 * 60 * 1000;
    let workingMaxData: any[][];
    if (maxData.length > 0 && !Array.isArray(maxData[0])) {
      const grouped = new Map<string, any[]>();
      (maxData as any[]).forEach((item) => {
        if (!item.datetimeutc) return;
        const dateStr = item.datetimeutc.split(" ")[0];
        if (!grouped.has(dateStr)) grouped.set(dateStr, []);
        grouped.get(dateStr)!.push(item);
      });
      workingMaxData = Array.from(grouped.values());
    } else {
      workingMaxData = maxData as any[][];
    }

    let globalFirstPoint: any = null;
    let globalFirstDate: Date | null = null;
    for (const dayData of original) {
      const dayArray = Array.isArray(dayData)
        ? dayData
        : Object.values(dayData);
      if (dayArray.length > 0) {
        globalFirstPoint = dayArray[0];
        globalFirstDate = new Date(globalFirstPoint.datetimeutc);
        if (globalFirstDate.toString() === "Invalid Date") {
          const parts = globalFirstPoint.datetimeutc.split(/[/ :]/);
          globalFirstDate = new Date(
            `${parts[2]}-${parts[0]}-${parts[1]} ${parts[3]}:${parts[4]}`,
          );
        }
        break;
      }
    }
    if (!globalFirstDate) return original;

    let filteredForecastData: any[] = [];
    original.forEach((dayData: any) => {
      const dayArray = Array.isArray(dayData)
        ? dayData
        : Object.values(dayData);
      if (dayArray.length === 0) return;
      let detectedInterval: number | null = null;
      if (dayArray.length >= 2) {
        let date1 = new Date(dayArray[0].datetimeutc);
        let date2 = new Date(dayArray[1].datetimeutc);
        if (date1.toString() === "Invalid Date") {
          const parts = dayArray[0].datetimeutc.split(/[/ :]/);
          date1 = new Date(
            `${parts[2]}-${parts[0]}-${parts[1]} ${parts[3]}:${parts[4]}`,
          );
        }
        if (date2.toString() === "Invalid Date") {
          const parts = dayArray[1].datetimeutc.split(/[/ :]/);
          date2 = new Date(
            `${parts[2]}-${parts[0]}-${parts[1]} ${parts[3]}:${parts[4]}`,
          );
        }
        detectedInterval = Math.round(
          (date2.getTime() - date1.getTime()) / MS_PER_HOUR,
        );
      }
      let isCompatible = false;
      if (dayArray.length === 1) isCompatible = true;
      else if (detectedInterval !== null && detectedInterval === maxHours)
        isCompatible = true;
      else if (
        detectedInterval !== null &&
        detectedInterval < maxHours &&
        maxHours % detectedInterval === 0
      )
        isCompatible = true;
      if (!isCompatible) return;

      let filteredDayData: any[] = [];
      dayArray.forEach((dataPoint: any) => {
        let currentDate = new Date(dataPoint.datetimeutc);
        if (currentDate.toString() === "Invalid Date") {
          const parts = dataPoint.datetimeutc.split(/[/ :]/);
          currentDate = new Date(
            `${parts[2]}-${parts[0]}-${parts[1]} ${parts[3]}:${parts[4]}`,
          );
        }
        const hoursFromGlobalFirst =
          (currentDate.getTime() - globalFirstDate!.getTime()) / MS_PER_HOUR;
        const remainder = hoursFromGlobalFirst % maxHours;
        const isAligned =
          Math.abs(remainder) < 0.5 || Math.abs(remainder - maxHours) < 0.5;
        if (isAligned) filteredDayData.push(dataPoint);
      });

      if (filteredDayData.length === 1) {
        let singleDate = new Date(filteredDayData[0].datetimeutc);
        if (singleDate.toString() === "Invalid Date") {
          const parts = filteredDayData[0].datetimeutc.split(/[/ :]/);
          singleDate = new Date(
            `${parts[2]}-${parts[0]}-${parts[1]} ${parts[3]}:${parts[4]}`,
          );
        }
        if (singleDate.getHours() === 0) return;
      }
      if (filteredDayData.length > 0)
        filteredForecastData.push(filteredDayData);
    });

    const DIRECTION_PAIRS: Record<string, string> = {
      a_10mwindspeed: "a_10mwinddir",
      a_50mwindspeed: "a_10mwinddir",
      a_80mwindspeed: "a_10mwinddir",
      a_100mwindspeed: "a_10mwinddir",
      a_10mgust: "a_10mwinddir",
      windseaheight: "windseadirection",
      swell1height: "swell1direction",
      swell2height: "swell2direction",
      sigwaveheight: "peakwavedir",
      maxwave: "peakwavedir",
      surfacecurrentspeed: "surfacecurrentdirection",
    };

    return filteredForecastData.map((forecastDay) => {
      if (!Array.isArray(forecastDay)) return forecastDay;
      const flatMaxData: any[] = [];
      workingMaxData.forEach((dayArray) => {
        if (Array.isArray(dayArray)) flatMaxData.push(...dayArray);
      });

      return forecastDay.map((forecastRow: any) => {
        let currentTime = new Date(forecastRow.datetimeutc);
        if (currentTime.toString() === "Invalid Date") {
          const parts = forecastRow.datetimeutc.split(/[/ :]/);
          currentTime = new Date(
            `${parts[2]}-${parts[0]}-${parts[1]} ${parts[3]}:${parts[4]}`,
          );
        }
        const window = flatMaxData.filter((maxRow: any) => {
          let t = new Date(maxRow.datetimeutc);
          if (t.toString() === "Invalid Date") {
            const parts = maxRow.datetimeutc.split(/[/ :]/);
            t = new Date(
              `${parts[2]}-${parts[0]}-${parts[1]} ${parts[3]}:${parts[4]}`,
            );
          }
          const diff = (currentTime.getTime() - t.getTime()) / MS_PER_HOUR;
          return diff >= 0 && diff < maxHours;
        });
        const result = { ...forecastRow };
        const maxSourceRows: { [key: string]: any } = {};
        Object.keys(forecastRow).forEach((key) => {
          if (key === "datetimeutc") return;
          if (typeof forecastRow[key] !== "number") return;
          let maxVal = forecastRow[key];
          let sourceRow = forecastRow;
          for (const maxRow of window) {
            if (typeof maxRow[key] === "number" && maxRow[key] > maxVal) {
              maxVal = maxRow[key];
              sourceRow = maxRow;
            }
          }
          result[key] = maxVal;
          maxSourceRows[key] = sourceRow;
        });
        Object.entries(DIRECTION_PAIRS).forEach(
          ([speedField, directionField]) => {
            if (
              maxSourceRows[speedField] &&
              maxSourceRows[speedField][directionField] !== undefined
            )
              result[directionField] =
                maxSourceRows[speedField][directionField];
          },
        );
        return result;
      });
    });
  }

  useEffect(() => {
    var datas = TableDatas;
    TableDatas.map((date: any) => {
      date.map((element: any) => {
        setOverViewColor((prevOverViewColor: any) => [
          ...prevOverViewColor,
          "red_overview",
        ]);
      });
    });
    datas.forEach((element: any) => {
      element.forEach((ele: any) => {
        ele.modelvisibility !== undefined
          ? modelvisibility.push(ele.modelvisibility)
          : modelvisibility.push(0);
      });
    });
    setMin(Math.floor(Math.min(...modelvisibility)));
    setMax(Math.floor(Math.max(...modelvisibility)));
  }, [TableDatas]);

  useEffect(() => {
    for (const key of timestraping) {
      if (selectText[key] === undefined)
        analysetheData(key, inputText[key], ">=");
      else analysetheData(key, inputText[key], selectText[key]);
    }
  }, [timestraping]);


function getColor(
  criteriaDetailDatas: CriteriaDetail[],
  data: DataRecord,
  SelectValue: string,
): string {
  const selectedCriteria = criteriaDetailDatas.filter(
    (c_data) => c_data.forecast_osf_criteria_id === parseInt(SelectValue, 10),
  );
  if (selectedCriteria.length === 0) return "white";

  const hasOrOperator = selectedCriteria.some(
    (c_data) => c_data.logical_operator_id === 2,
  );

  const colorResults = selectedCriteria.map((c_data) =>
    getColorForField(
      data[c_data.field_name],
      parseFloat(c_data.margin_value),
      parseFloat(c_data.value),
      c_data.logical_operator_id,  // ← pass null as-is, handle in getColorForField
    ),
  );

  if (hasOrOperator) {
    if (colorResults.includes("red_overview")) return "red_overview";
    else if (colorResults.includes("green_overview")) {
      if (colorResults.includes("yellow_overview")) return "yellow_overview";
      return "green_overview";
    } else if (colorResults.every((color) => color === "yellow_overview"))
      return "yellow_overview";
  } else {
    if (colorResults.includes("red_overview")) return "red_overview";
    else if (colorResults.includes("yellow_overview")) return "yellow_overview";
    else if (colorResults.every((color) => color === "green_overview"))
      return "green_overview";
  }
  return "white";
}


function getColorForField(
  data: number | undefined | null,
  marginValue: number,
  value: number,
  comparisonOperatorId: number | null,  // ← accept null
): string {
  if (data === undefined || data === null) return "white";

  if (comparisonOperatorId === null) {
    return data > value
      ? "red_overview"
      : data > marginValue
        ? "yellow_overview"
        : "green_overview";
  }

  switch (comparisonOperatorId) {
    case 1:
      return data < value
        ? "red_overview"
        : data < marginValue
          ? "yellow_overview"
          : "green_overview";
    case 2:
      return data > value
        ? "red_overview"
        : data > marginValue
          ? "yellow_overview"
          : "green_overview";
    case 3:
      return data <= value
        ? data <= marginValue
          ? "green_overview"
          : "yellow_overview"
        : "red_overview";
    case 4:
      return data >= value
        ? data >= marginValue
          ? "green_overview"
          : "yellow_overview"
        : "red_overview";
    case 5:
      return data === value ? "green_overview" : "red_overview";
    case 6:
      return data !== value ? "green_overview" : "red_overview";
    case 7:
      return data > marginValue && data < value
        ? "green_overview"
        : "red_overview";
    case 8:
      return data === null ? "green_overview" : "red_overview";
    case 9:
      return data !== null ? "green_overview" : "red_overview";
    default:
      return "white";
  }
}

  function getDataValue(value: any, arg: string): any {
    if (isNaN(parseFloat(value))) return parseFloat(value);
    if (arg === "surfacecurrentspeed") return Number(Number(value).toFixed(1));
    else if (arg === "sigwaveheight")
      return Number((Math.round(Number(value) * 10) / 10).toFixed(1));
    else if (arg === "swell1period" || arg === "swell2period")
      return Math.round(Number(value));
    else if (arg === "swell1height" || arg === "swell2height")
      return value % 1 === 0 ? value : Number(value).toFixed(1);
    else if (arg === "windseaheight" || arg === "windseaperiod") {
      if (typeof value === "string" && value.length === 16)
        return parseFloat(value.slice(10));
      else
        return Number(value) % 1 === 0
          ? value
          : Math.round(Number(value) * 10) / 10;
    } else if (
      arg === "a_10mwindspeed" ||
      arg === "a_10mgust" ||
      arg === "a_50mwindspeed" ||
      arg === "a_80mwindspeed" ||
      arg === "a_100mwindspeed"
    ) {
      if (typeof value === "string" && value.length === 16)
        return parseFloat(value.slice(10));
      else return Math.round(value);
    } else {
      return parseFloat(value);
    }
  }

  function analysetheData(
    arg: string,
    text: string,
    mode: string,
    updateOverview = true,
  ) {
    let table: any = { ...tableColorDatas };
    Object.keys(table).forEach(
      (key) => key.slice(6, key.lastIndexOf("_")) === arg && delete table[key],
    );
    const inputValue = parseFloat(text);
    const inputIsEmpty = !text || text.trim() === "";
    let total_index = 0;
    TableDatas.forEach((datas: any) => {
      for (const eachinner in datas) {
        let color = "white";
        if (!inputIsEmpty) {
          const dataValue = getDataValue(datas[eachinner][arg], arg);
          if (!isNaN(dataValue)) {
            if (
              (mode === ">=" && dataValue >= inputValue) ||
              (mode === "<=" && dataValue <= inputValue)
            )
              color = mode === ">=" ? "red" : "green";
          }
        }
        table["color_" + arg + "_" + total_index] = color;
        total_index++;
      }
    });
    if (updateOverview) {
      const newColorArray: any = [];
      TableDatas.forEach((date: any) => {
        date.forEach((element: any) => {
          let data = parseFloat(element.a_10mwindspeed);
          let color = "white";
          let criteria =
            criteriaDetailDatas.find(
              (c_data: any) =>
                c_data.forecast_osf_criteria_id === parseInt(SelectValue),
            ) || {};
          if (
            (data >= criteria.value && data <= criteria.margin_value) ||
            (data <= criteria.value && data >= criteria.margin_value)
          )
            color = "yellow_overview";
          else if (data > criteria.value && data > criteria.margin_value)
            color = "red_overview";
          else if (data < criteria.value && data < criteria.margin_value)
            color = "green_overview";
          newColorArray.push(color);
        });
      });
    }
    setTableColorDatas(table);
  }

  function analysetheFreq(arg: string[], text: string, mode: string) {
    const direction = [
      "a_10winddir",
      "windseadirection",
      "swell1direction",
      "swell2direction",
      "peakwavedir",
      "surfacecurrentdirection",
    ];
    let table: any = { ...tableColorDatas };
    arg.forEach((element) => {
      for (const s in inputText) {
        if (!direction.includes(element) && element === s) {
          Object.keys(table).forEach(
            (data) =>
              data.slice(6, data.lastIndexOf("_")) === element &&
              delete table[data],
          );
          let total_index = 0;
          TableDatas.forEach((datas: any) => {
            for (const eachinner in datas) {
              const inputValue = getDataValue(
                datas[eachinner][element],
                element,
              );
              const boolean_value =
                mode === ">="
                  ? inputValue >= parseFloat(inputText[s])
                  : inputValue <= parseFloat(inputText[s]);
              table["color_" + element + "_" + total_index] = boolean_value
                ? mode === ">="
                  ? "red"
                  : "green"
                : "white";
              total_index++;
            }
          });
        }
      }
    });
    setTableColorDatas((prev: any) => table);
  }

  const tableValueProps = {
    inputText,
    selectText,
    inputRefValue,
    wind,
    windwave,
    swell1,
    swell2,
    total,
    currents,
    setInputText,
    setSelectText,
    setInputRefValue,
    setRender,
    analysetheData,
    updateAgain: (name: string, mode: string) => {
      const wc = [
        "a_10mgust",
        "a_10mwindspeed",
        "a_50mwindspeed",
        "a_80mwindspeed",
        "a_100mwindspeed",
        "a_10mwinddir",
      ];
      const wv = ["windseadirection", "windseaheight", "windseaperiod"];
      const s1 = ["swell1height", "swell1direction", "swell1period"];
      const s2 = ["swell2height", "swell2direction", "swell2period"];
     const tc = ["sigwaveheight", "maxwave", "peakwavedir", "totalseadir_virtual", "peakwaveperiod"];
      const cc = ["surfacecurrentdirection", "surfacecurrentspeed"];
      if (wc.includes(name)) analysetheFreq(wc, inputText, mode);
      else if (wv.includes(name)) analysetheFreq(wv, inputText, mode);
      else if (s1.includes(name)) analysetheFreq(s1, inputText, mode);
      else if (s2.includes(name)) analysetheFreq(s2, inputText, mode);
      else if (tc.includes(name)) analysetheFreq(tc, inputText, mode);
      else if (cc.includes(name)) analysetheFreq(cc, inputText, mode);
    },
  };

  function Dateformat(celldata: any) {
    const dateObject = new Date(celldata);
    const inputDate = dateObject;
    const dayc = dayNames[inputDate.getDay()];
    const monthc = monthNames[inputDate.getMonth()];
    const yearc = inputDate.getFullYear();
    return `${dayc}, ${inputDate.getDate()} ${monthc} ${yearc}`;
  }

  useEffect(() => {
    setTableContent(ScrapeDatas2(tableColorDatas).map((d: any) => d));
  }, [overViewColor, tableColorDatas, TableDatas]);

  function ScrapeDatas(datas: any, index: number, tableColorDatas: any = {}) {
    let total_index = index;
    var dict: any = [];
    var dates: any = [];
    let tmpdir: any;
    for (const data_array in datas) {
      var dict_temp: any = [];
      let first = true;
      let dayformat = true;
      const rowKey = datas[data_array]?.datetimeutc ?? `row_${total_index}`;

      for (let data in datas[data_array]) {
        let cellData = datas[data_array][data];
        if (data === "datetimeutc") {
          var weatherDate = new Date(cellData + " UTC");
          let groupDate = new Date(weatherDate);
          groupDate.setUTCHours(0, 0, 0, 0);
          if (
            !dates.some((date: Date) => date.getTime() === groupDate.getTime())
          ) {
            dates.push(groupDate);
            dict.push(
              <tr key={`sep_${rowKey}`}>
                <td
                  style={{
                    textAlign: "center",
                    fontWeight: "500",
                    borderTop: "1px solid #437c92",
                    maxHeight: "4px",
                    borderBottom: "1px solid #437c92",
                    borderRight: "0.5px solid gray",
                  }}
                  colSpan={100}
                >
                  {cellData === null || cellData === undefined
                    ? "-"
                    : Dateformat(groupDate)}
                  {(dayformat = false)}
                </td>
              </tr>,
            );
          }
          if (showWxWindow) {
            dict_temp.push(
              <td
                style={{ textAlign: "center" }}
                className={`${tableColorDatas["color_" + data + "_" + total_index] === undefined ? "-" : tableColorDatas["color_" + data + "_" + total_index]}`}
                key={`${rowKey}_wx`}
              >
                {cellData === null || cellData === undefined ? (
                  "-"
                ) : (
                  <div
                    style={{
                      display: "flex",
                      gap: "8px",
                      justifyContent: "center",
                    }}
                  >
                    <div
                      style={{
                        width: "20px",
                        height: "20px",
                        display: "flex",
                        justifyContent: "center",
                        borderRadius: "3px",
                      }}
                      className={overViewColor[incrRef.current]}
                    ></div>
                  </div>
                )}
              </td>,
            );
          }
          dict_temp.push(
            <td
              style={{ textAlign: "center", borderLeft: "0.5px solid gray" }}
              key={`${rowKey}_datetimeutc`}
            >
              {cellData === null || cellData === undefined ? (
                "-"
              ) : (
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    justifyContent: "center",
                  }}
                >
                  {`${weatherDate.getUTCDate()}/${weatherDate.toISOString().slice(11, 13)}${weatherDate.toISOString().slice(14, 16)}`}
                </div>
              )}
            </td>,
          );
        }
      }

      first = true;
      const latlong = ["lat", "long"];
      // eslint-disable-next-line no-loop-func
      latlong.forEach((columnName1) => {
        let found = false;
        for (let data in datas[data_array]) {
          let cellData = datas[data_array][data];
          if (data === columnName1) {
            found = true;
            dict_temp.push(
              <td
                style={{
                  borderLeft: `${first === true ? "0.5px solid gray" : ""}`,
                  textAlign: "center",
                }}
                key={`${rowKey}_${columnName1}`}
              >
                {cellData === null || cellData === undefined ? (
                  "-"
                ) : typeof cellData === "string" && cellData.length === 16 ? (
                  <div style={{ display: "flex", gap: "8px" }}>
                    <div
                      className={`${overViewColor[incrRef.current]} tableContent`}
                    >
                      {cellData.slice(10)}
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: "20px",
                      display: "flex",
                      justifyContent: "center",
                      borderRadius: "3px",
                    }}
                    className={`${tableColorDatas["color_" + data + "_" + total_index] === undefined ? "-" : tableColorDatas["color_" + data + "_" + total_index]}`}
                  >
                    {typeof cellData === "number"
                      ? cellData.toFixed(1)
                      : cellData}
                  </div>
                )}
                {(first = false)}
              </td>,
            );
          }
        }
        if (found) setLatLang(true);
      });

      for (let data in datas[data_array]) {
        let cellData: any = datas[data_array][data];
        if (data === "a_10mwinddir") {
          cellData = calculateWindDir(datas[data_array][data]);
          tmpdir = calculateWindDir(datas[data_array][data]);
        }
        if (
          data === "modelvisibility" ||
          data === "cloudbase" ||
          data === "rainrate"
        )
          continue;
        if (data === "a_10mwinddir") {
          dict_temp.push(
            <td
              style={{ borderLeft: "0.5px solid gray", textAlign: "center" }}
              key={`${rowKey}_a_10mwinddir`}
            >
              {cellData === null || cellData === undefined ? (
                "-"
              ) : typeof cellData === "string" && cellData.length === 16 ? (
                <div style={{ display: "flex", gap: "8px" }}>
                  <div
                    style={{
                      width: "100%",
                      height: "20px",
                      display: "flex",
                      justifyContent: "center",
                      borderRadius: "3px",
                    }}
                    className={overViewColor[incrRef.current]}
                  >
                    {cellData.slice(10)}
                  </div>
                </div>
              ) : (
                cellData
              )}
            </td>,
          );
        }
      }

      for (let data in datas[data_array]) {
        let cellData: any = datas[data_array][data];
        if (data === "a_10mwinddir") {
          cellData = calculateWindDir(datas[data_array][data]);
          tmpdir = calculateWindDir(datas[data_array][data]);
        }
        if (
          data === "modelvisibility" ||
          data === "cloudbase" ||
          data === "rainrate"
        )
          continue;
        if (
          data === "a_10mwindspeed" ||
          data === "a_10mgust" ||
          data === "a_50mwindspeed" ||
          data === "a_80mwindspeed" ||
          data === "a_100mwindspeed"
        ) {
          dict_temp.push(
            <td
              style={{ borderRight: `${""}`, textAlign: "center" }}
              key={`${rowKey}_${data}`}
            >
              {cellData === null || cellData === undefined ? (
                "-"
              ) : typeof cellData === "string" && cellData.length === 16 ? (
                <div style={{ display: "flex", gap: "8px" }}>
                  <div
                    style={{
                      width: "100%",
                      height: "20px",
                      display: "flex",
                      justifyContent: "center",
                      borderRadius: "3px",
                    }}
                    className={overViewColor[incrRef.current]}
                  >
                    {cellData.slice(10)}
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "20px",
                    display: "flex",
                    justifyContent: "center",
                    borderRadius: "3px",
                  }}
                >
                  <p
                    className={`${tableColorDatas["color_" + data + "_" + total_index] === undefined ? "-" : tableColorDatas["color_" + data + "_" + total_index]}`}
                  >
                    {Math.round(cellData)}
                  </p>
                </div>
              )}
            </td>,
          );
        }
      }

      const hasWindWaveData =
        datas[data_array].hasOwnProperty("windseadirection") ||
        datas[data_array].hasOwnProperty("windseaheight") ||
        datas[data_array].hasOwnProperty("windseaperiod");

      if (hasWindWaveData) {
        let directionWw = false;
        for (let data in datas[data_array]) {
          let cellData = datas[data_array][data];
          if (data === "windseadirection") {
            cellData = calculateWindDir(datas[data_array][data]);
            directionWw = true;
            setPreWw(true);
            dict_temp.push(
              <td
                style={{
                  borderLeft: "0.5px solid #437c92",
                  textAlign: "center",
                }}
                key={`${rowKey}_windseadirection`}
              >
                {cellData === null || cellData === undefined ? (
                  "-"
                ) : typeof cellData === "string" && cellData.length === 16 ? (
                  <div style={{ display: "flex", gap: "8px" }}>
                    {cellData.slice(10)}
                  </div>
                ) : (
                  <>{cellData}</>
                )}
              </td>,
            );
          }
        }
        if (!directionWw) {
          setIsDirWw(true);
          dict_temp.push(
            <td
              style={{ borderLeft: "0.5px solid #437c92", textAlign: "center" }}
              key={`${rowKey}_windseadirection_empty`}
            >
              -
            </td>,
          );
        }
      }

      for (let data in datas[data_array]) {
        let cellData = datas[data_array][data];
        if (
          hasWindWaveData &&
          (data === "windseaheight" || data === "windseaperiod")
        ) {
          dict_temp.push(
            <td
              style={{ borderLeft: `${""}`, textAlign: "center" }}
              key={`${rowKey}_${data}`}
            >
              {cellData === null || cellData === undefined ? (
                "-"
              ) : typeof cellData === "string" && cellData.length === 16 ? (
                <div style={{ display: "flex", gap: "8px" }}>
                  <div
                    style={{
                      width: "100%",
                      height: "20px",
                      display: "flex",
                      justifyContent: "center",
                      borderRadius: "3px",
                    }}
                    className={overViewColor[incrRef.current]}
                  >
                    {cellData.slice(10)}
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "20px",
                    display: "flex",
                    justifyContent: "center",
                    borderRadius: "3px",
                    textAlign: "center",
                  }}
                >
                  <p
                    className={`${tableColorDatas["color_" + data + "_" + total_index] === undefined ? "-" : tableColorDatas["color_" + data + "_" + total_index]}`}
                  >
                    {Number(cellData) % 1 === 0
                      ? cellData
                      : Math.round(Number(cellData) * 10) / 10}
                  </p>
                </div>
              )}
              {(first = false)}
            </td>,
          );
        }
      }

      first = true;
      let directionS1 = false;
      for (let data in datas[data_array]) {
        let cellData = datas[data_array][data];
        if (data === "a_10mwinddir") {
          cellData = calculateWindDir(datas[data_array][data]);
          tmpdir = calculateWindDir(datas[data_array][data]);
        }
        if (
          data === "modelvisibility" ||
          data === "cloudbase" ||
          data === "rainrate"
        )
          continue;
        if (data === "swell1direction") {
          directionS1 = true;
          let swellDirection = datas[data_array][data];
          dict_temp.push(
            <td
              style={{
                borderLeft: "0.5px solid #437c92",
                borderRight: `${""}`,
                textAlign: "center",
              }}
              key={`${rowKey}_swell1direction`}
            >
              {swellDirection === null ||
              swellDirection === undefined ||
              swellDirection === 0 ? (
                "-"
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <span>{calculateWindDir(swellDirection)}</span>
                </div>
              )}
            </td>,
          );
        }
      }
      if (directionS1 === false && swell1 !== 0) {
        setPreS1(true);
        dict_temp.push(
          <td
            style={{
              borderLeft: "0.5px solid #437c92",
              borderRight: `${""}`,
              textAlign: "center",
            }}
            key={`${rowKey}_swell1direction_empty`}
          >
            <></>
          </td>,
        );
      }
      for (let data in datas[data_array]) {
        let cellData = datas[data_array][data];
        if (data === "swell1height" || data === "swell1period") {
          dict_temp.push(
            <td style={{ textAlign: "center" }} key={`${rowKey}_${data}`}>
              {cellData === null || cellData === undefined || cellData === 0 ? (
                "-"
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "20px",
                    display: "flex",
                    justifyContent: "center",
                    borderRadius: "3px",
                  }}
                >
                  <p
                    className={`${tableColorDatas["color_" + data + "_" + total_index] === undefined ? "-" : tableColorDatas["color_" + data + "_" + total_index]}`}
                  >
                    {data === "swell1period"
                      ? Math.round(Number(cellData))
                      : cellData % 1 === 0
                        ? cellData
                        : Number(cellData).toFixed(1)}
                  </p>
                </div>
              )}
              {(first = false)}
            </td>,
          );
        }
        if (data === "Tp" || data === "Hs") {
          dict_temp.push(
            <td style={{ textAlign: "center" }} key={`${rowKey}_${data}`}>
              {cellData === null || cellData === undefined || cellData === 0 ? (
                "-"
              ) : (
                <p
                  className={`${tableColorDatas["color_" + data + "_" + total_index] === undefined ? "-" : tableColorDatas["color_" + data + "_" + total_index]}`}
                >
                  {data === "Tp"
                    ? Math.round(Number(cellData))
                    : data === "Hs" || data === "swell1height"
                      ? cellData === null || cellData === undefined
                        ? "-"
                        : `${Number(cellData).toFixed(1)}`
                      : cellData}
                </p>
              )}
            </td>,
          );
        }
      }

      first = true;
      let directionS2 = false;
      for (let data in datas[data_array]) {
        let cellData = datas[data_array][data];
        if (data === "swell2direction") {
          directionS2 = true;
          dict_temp.push(
            <td
              style={{
                borderLeft: `${first === true ? "0.5px solid gray" : ""}`,
                textAlign: "center",
              }}
              key={`${rowKey}_swell2direction`}
            >
              {cellData === null || cellData === undefined || cellData === 0 ? (
                "-"
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "20px",
                    display: "flex",
                    justifyContent: "center",
                    borderRadius: "3px",
                  }}
                >
                  <p
                    className={`${tableColorDatas["color_" + data + "_" + total_index] === undefined ? "-" : tableColorDatas["color_" + data + "_" + total_index]}`}
                  >
                    {getCompassDirection(Number(cellData))}
                  </p>
                </div>
              )}
              {(first = false)}
            </td>,
          );
        }
      }
      if (directionS2 === false && swell2 !== 0) {
        setPreS2(true);
        dict_temp.push(
          <td
            style={{
              borderLeft: "0.5px solid #437c92",
              borderRight: `${""}`,
              textAlign: "center",
            }}
            key={`${rowKey}_swell2direction_empty`}
          >
            <></>
          </td>,
        );
      }
      for (let data in datas[data_array]) {
        let cellData = datas[data_array][data];
        if (data === "swell2height" || data === "swell2period") {
          dict_temp.push(
            <td
              style={{
                borderLeft: `${first === true ? "0.5px solid gray" : ""}`,
                textAlign: "center",
              }}
              key={`${rowKey}_${data}`}
            >
              {cellData === null || cellData === undefined || cellData === 0 ? (
                "-"
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "20px",
                    display: "flex",
                    justifyContent: "center",
                    borderRadius: "3px",
                  }}
                >
                  <p
                    className={`${tableColorDatas["color_" + data + "_" + total_index] === undefined ? "-" : tableColorDatas["color_" + data + "_" + total_index]}`}
                  >
                    {data === "swell2period"
                      ? Math.round(Number(cellData))
                      : Number(cellData) % 1 === 0
                        ? cellData
                        : Number(cellData).toFixed(1)}
                  </p>
                </div>
              )}
              {(first = false)}
            </td>,
          );
        }
      }

      first = true;
      let directionTS = false;
      for (let data in datas[data_array]) {
        let cellData = datas[data_array][data];
        if (data === "peakwavedir") {
          directionTS = true;
          dict_temp.push(
            <td
              style={{
                borderLeft: "0.5px solid #437c92",
                borderRight: `${""}`,
                textAlign: "center",
              }}
              key={`${rowKey}_peakwavedir`}
            >
              {cellData === null || cellData === undefined ? (
                "-"
              ) : typeof cellData === "string" && cellData.length === 16 ? (
                <div style={{ display: "flex", gap: "8px" }}>
                  <div
                    style={{
                      width: "100%",
                      height: "20px",
                      display: "flex",
                      justifyContent: "center",
                      borderRadius: "3px",
                    }}
                  >
                    {cellData.slice(10)}
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "20px",
                    display: "flex",
                    justifyContent: "center",
                    borderRadius: "3px",
                  }}
                  className={`${tableColorDatas["color_peakwavedir_" + total_index] || ""}`}
                >
                  {calculateWindDir(Number(cellData))}
                </div>
              )}
            </td>,
          );
        }
      }
      if (!directionTS && total !== 0) {
        dict_temp.push(
          <td
            style={{ borderLeft: "0.5px solid #437c92", textAlign: "center" }}
            key={`${rowKey}_totalseadir_virtual`}
          >
            -
          </td>,
        );
      }
      for (let data in datas[data_array]) {
        let cellData = datas[data_array][data];
        if (
          data === "sigwaveheight" ||
          data === "peakwaveperiod" ||
          data === "maxwave"
        ) {
          dict_temp.push(
            <td
              style={{
                borderLeft: `${first && data === "sigwaveheight" ? "0.5px solid gray" : ""}`,
                textAlign: "center",
              }}
              key={`${rowKey}_${data}`}
            >
              {cellData === null || cellData === undefined ? (
                "-"
              ) : typeof cellData === "string" && cellData.length === 16 ? (
                <div style={{ display: "flex", gap: "8px" }}>
                  <div
                    className={`${overViewColor[incrRef.current]} tableContent`}
                  >
                    {cellData.slice(10)}
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "20px",
                    display: "flex",
                    justifyContent: "center",
                    borderRadius: "3px",
                  }}
                >
                  <p
                    className={`${tableColorDatas["color_" + data + "_" + total_index] || "-"}`}
                  >
                    {data === "maxwave"
                      ? Number(cellData).toFixed(1)
                      : data === "peakwaveperiod"
                        ? Math.round(Number(cellData))
                        : Number(cellData) % 1 === 0
                          ? cellData
                          : (Math.round(Number(cellData) * 10) / 10).toFixed(1)}
                  </p>
                </div>
              )}
              {(first = false)}
            </td>,
          );
        }
      }

      first = true;
      const weatherColumns = [
        "a_2mtemp",
        "a_2mrelativehumidity",
        "heatindex",
        "cloudbase",
        "modelvisibility",
        "totalcloud",
        "rainrate",
        "totalprecip",
        "mslp",
        "weather_text",
      ];
      // eslint-disable-next-line no-loop-func
      weatherColumns.forEach((columnName) => {
        for (let data in datas[data_array]) {
          let cellData = datas[data_array][data];
          if (data === columnName) {
            dict_temp.push(
              <td
                style={{
                  borderLeft: `${first === true ? "0.5px solid gray" : ""}`,
                  textAlign: "center",
                }}
                key={`${rowKey}_${data}`}
              >
                {cellData === null || cellData === undefined ? (
                  "-"
                ) : typeof cellData === "string" && cellData.length === 16 ? (
                  <div style={{ display: "flex", gap: "8px" }}>
                    <div
                      className={`${overViewColor[incrRef.current]} tableContent`}
                    >
                      {cellData.slice(10)}
                    </div>
                  </div>
                ) : (
                  <div
                    style={{
                      width: "100%",
                      height: "20px",
                      display: "flex",
                      justifyContent: "center",
                      borderRadius: "3px",
                    }}
                    className={`${tableColorDatas["color_" + data + "_" + total_index] === undefined ? "-" : tableColorDatas["color_" + data + "_" + total_index]}`}
                  >
                    {data === "weather_text"
                      ? cellData
                      : data === "a_2mrelativehumidity"
                        ? Math.round(Number(cellData))
                        : data === "totalcloud"
                          ? Number(cellData).toFixed(1)
                          : data === "heatindex"
                            ? Math.round(Number(cellData))
                            : data === "modelvisibility"
                              ? parseFloat(cellData).toFixed(1)
                              : data === "totalprecip"
                                ? parseFloat(cellData).toFixed(1)
                                : data === "cloudbase"
                                  ? Math.ceil(cellData * 0.3048)
                                  : Math.floor(cellData)}
                  </div>
                )}
                {(first = false)}
              </td>,
            );
          }
        }
      });

      let firstCurrent = true;
      let directionCurrent = false;
      for (let data in datas[data_array]) {
        let cellData = datas[data_array][data];
        if (data === "surfacecurrentdirection") {
          directionCurrent = true;
          dict_temp.push(
            <td
              style={{ borderLeft: "0.5px solid gray", textAlign: "center" }}
              key={`${rowKey}_surfacecurrentdirection`}
            >
              {cellData === null || cellData === undefined ? (
                "-"
              ) : cellData === 0 ? (
                "0"
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "20px",
                    display: "flex",
                    justifyContent: "center",
                    borderRadius: "3px",
                  }}
                >
                  <p
                    className={
                      tableColorDatas["color_" + data + "_" + total_index] ===
                      undefined
                        ? "-"
                        : tableColorDatas["color_" + data + "_" + total_index]
                    }
                  >
                    {calculateWindDir(Number(cellData))}
                  </p>
                </div>
              )}
            </td>,
          );
        }
      }

      for (let data in datas[data_array]) {
        let cellData = datas[data_array][data];
        if (data === "surfacecurrentspeed") {
          dict_temp.push(
            <td
              style={{
                borderLeft: `${firstCurrent === true ? "0.5px solid gray" : ""}`,
                textAlign: "center",
              }}
              key={`${rowKey}_surfacecurrentspeed`}
            >
              {cellData === null || cellData === undefined ? (
                "-"
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "20px",
                    display: "flex",
                    justifyContent: "center",
                    borderRadius: "3px",
                  }}
                >
                  <p
                    className={`${tableColorDatas["color_" + data + "_" + total_index] === undefined ? "-" : tableColorDatas["color_" + data + "_" + total_index]}`}
                  >
                    {Number(cellData) % 1 === 0
                      ? cellData
                      : Number(cellData).toFixed(1)}
                  </p>
                </div>
              )}
              {(firstCurrent = false)}
            </td>,
          );
        }
      }

      dict.push(
        <tr
          style={{
            background: `${incrRef.current % 2 === 0 ? "#ADD8E6" : "#FFFFFFF"}`,
          }}
          key={`${rowKey}_row`}
          className="tabledetail"
        >
          {dict_temp.map((s: any) => s)}
        </tr>,
      );
      total_index += 1;
      incrRef.current += 1;
    }
    return dict;
  }

  function ScrapeDatas2(tableColorDatas: any = {}) {
    incrRef.current = 0;
    let total_index = 0;
    let dict: any = [];
    TableDatas.forEach((data: any) => {
      ScrapeDatas(data, total_index, tableColorDatas).forEach((d: any) => {
        dict.push(d);
        total_index += 1;
      });
      total_index -= 1;
    });
    return dict;
  }

  function tableHeaderFn(caption: any) {
    var text;
    switch (caption) {
      case "Time":
        text = "UTC";
        break;
      case "10m Wind Dir":
        text = "Dir";
        break;
      case "10m Wind Speed":
        text = "Ws10m";
        break;
      case "10m Gust":
        text = "Wg10m";
        break;
      case "50m Wind Speed":
        text = "Ws50m";
        break;
      case "100m Wind Speed":
        text = "Ws100m";
        break;
      case "Wind Wave Height":
        text = "Hs";
        break;
      case "Wind Wave Direction":
        text = "Dir";
        break;
      case "totalprecip":
        text = "Accumulate";
        break;
      case "Swell 1 Dir":
        text = "Dir";
        break;
      case "Swell 2 Dir":
        text = "Dir";
        break;
      case "Swell 1 Height":
        text = "Hs";
        break;
      case "Swell 2 Height":
        text = "Hs";
        break;
      case "Sig Wave Height":
        text = "Hs";
        break;
      case "Peak Wave Period":
        text = "Tp";
        break;
      case "Max Wave":
        text = "Hmax";
        break;
      case "Surface Current Dir Towards":
        text = "Dir";
        break;
      case "Surface Current Speed":
        text = "Sp";
        break;
      case "Visibility":
        text = "Vis";
        break;
      case "Wind Wave Period":
        text = "Tp";
        break;
      case "Swell 1 Period":
        text = "Tp";
        break;
      case "2m Temp":
        text = "2m Temp";
        break;
      case "MSLP":
        text = "MSLP";
        break;
      case "Cloud Base":
        text = "Cloud Base";
        break;
      case "Amt":
        text = "Amt";
        break;
      case "Peak Wave Dir":
        text = "Dir";
        break;
      case "Swell 2 Period":
        text = "Tp";
        break;
      case "Accummulated Precip":
        text = "Amt";
        break;
      case "2m RH":
        text = "2m RH";
        break;
      case "Heat Index":
        text = "Heat Index";
        break;
      case "Total Cloud Amount":
        text = "Total Cloud";
        break;
      case "Weather Text":
        text = "Wx";
        break;
      default:
        text = caption;
    }
    return text;
  }

  function HeaderFunction() {
    let headers = new Array();
    const timeZone = forecastDatas?.[0]?.[0]?.time_zone || "(UTC+00:00)";
    const timeZoneDisplay =
      timeZone.match(/\(UTC[^\)]+\)/)?.[0] || "(UTC+00:00)";

    tableHeader.map((column: any, index: number) => {
      if (column.name === "datetimeutc") {
        headers.push(
          <td key={column.name} style={hBlue}>
            <strong>{timeZoneDisplay}</strong>
          </td>,
        );
      }
    });

    if (latLand) {
      headers.push(<td key="lat-hf" style={hBlue} />);
      headers.push(<td key="lon-hf" style={hBlue} />);
    }

    tableHeader.map((column: any, index: number) => {
      if (column.name === "a_10mwinddir") {
        const replace =
          column.output_unit_name === "kts" || column.ud_unit_name === "kt"
            ? "kn"
            : column.ud_unit_name === "None"
              ? " - "
              : column.ud_unit_name;
        headers.push(
          <td key={column.name} style={hBlue}>
            <TableTitle
              caption={<strong>{tableHeaderFn(column.caption)}</strong>}
              subCaption={replace}
              name={column.name}
            />
          </td>,
        );
      }
    });
    tableHeader.map((column: any, index: number) => {
      if (
        column.name === "a_10mwindspeed" ||
        column.name === "a_10mgust" ||
        column.name === "a_50mwindspeed" ||
        column.name === "a_80mwindspeed" ||
        column.name === "a_100mwindspeed"
      ) {
        const replace =
          column.output_unit_name === "kts" || column.ud_unit_name === "kt"
            ? "kn"
            : column.ud_unit_name === "None"
              ? " - "
              : column.ud_unit_name;
        headers.push(
          <td key={column.name} style={hBlue}>
            <TableTitle
              caption={<strong>{tableHeaderFn(column.caption)}</strong>}
              subCaption={replace}
              name={column.name}
            />
          </td>,
        );
      }
    });

    let directionWw = false;
    tableHeader.map((column: any, index: number) => {
      if (column.name === "windseadirection" && windwave > 0) {
        directionWw = true;
        const replace =
          column.output_unit_name === "kts" || column.ud_unit_name === "kt"
            ? "kn"
            : column.ud_unit_name === "None"
              ? " - "
              : column.ud_unit_name;
        headers.push(
          <td key={column.name} style={hBlueL}>
            <TableTitle
              caption={<b>{tableHeaderFn(column.caption)}</b>}
              subCaption={replace}
              name={column.name}
            />
          </td>,
        );
      }
    });
    if (directionWw === false && windwave > 0) {
      headers.push(
        <td key="ww-dir-fallback-hf" style={hBlueL}>
          <TableTitle caption={<strong>Dir</strong>} subCaption="" name="Dir" />
        </td>,
      );
    }
    tableHeader.map((column: any, index: number) => {
      if (
        (column.name === "windseaheight" || column.name === "windseaperiod") &&
        windwave > 0
      ) {
        const replace =
          column.output_unit_name === "kts" || column.ud_unit_name === "kt"
            ? "kn"
            : column.ud_unit_name === "None"
              ? " - "
              : column.ud_unit_name;
        headers.push(
          <td key={column.name} style={hBlue}>
            <TableTitle
              caption={<strong>{tableHeaderFn(column.caption)}</strong>}
              subCaption={replace}
              name={column.name}
            />
          </td>,
        );
      }
    });

    let directionS1 = false;
    tableHeader.map((column: any, index: number) => {
      if (column.name === "swell1direction") {
        directionS1 = true;
        const replace =
          column.output_unit_name === "kts" || column.ud_unit_name === "kt"
            ? "kn"
            : column.ud_unit_name === "None"
              ? " - "
              : column.ud_unit_name;
        headers.push(
          <td key={column.name} style={hBlueL}>
            <TableTitle
              caption={<strong>{tableHeaderFn(column.caption)}</strong>}
              subCaption={replace}
              name={column.name}
            />
          </td>,
        );
      }
    });
    if (directionS1 === false && swell1 !== 0) {
      headers.push(
        <td key="s1-dir-fallback-hf" style={hBlueL}>
          <TableTitle caption="Dir" subCaption="" name="Dir" />
        </td>,
      );
    }
    tableHeader.map((column: any, index: number) => {
      if (column.name === "swell1height" || column.name === "swell1period") {
        const replace =
          column.output_unit_name === "kts" || column.ud_unit_name === "kt"
            ? "kn"
            : column.ud_unit_name === "None"
              ? " - "
              : column.ud_unit_name;
        headers.push(
          <td key={column.name} style={hBlue}>
            <TableTitle
              caption={<strong>{tableHeaderFn(column.caption)}</strong>}
              subCaption={replace}
              name={column.name}
            />
          </td>,
        );
      }
    });

    let directionS2 = false;
    tableHeader.map((column: any, index: number) => {
      if (column.name === "swell2direction") {
        directionS2 = true;
        const replace =
          column.output_unit_name === "kts" || column.ud_unit_name === "kt"
            ? "kn"
            : column.ud_unit_name === "None"
              ? " - "
              : column.ud_unit_name;
        headers.push(
          <td key={column.name} style={hBlueL}>
            <TableTitle
              caption={<strong>{tableHeaderFn(column.caption)}</strong>}
              subCaption={replace}
              name={column.name}
            />
          </td>,
        );
      }
    });
    if (directionS2 === false && swell2 !== 0) {
      headers.push(
        <td key="s2-dir-fallback-hf" style={hBlueL}>
          <TableTitle caption="Dir" subCaption="" name="Dir" />
        </td>,
      );
    }
    tableHeader.map((column: any, index: number) => {
      if (column.name === "swell2height" || column.name === "swell2period") {
        const replace =
          column.output_unit_name === "kts" || column.ud_unit_name === "kt"
            ? "kn"
            : column.ud_unit_name === "None"
              ? " - "
              : column.ud_unit_name;
        headers.push(
          <td key={column.name} style={hBlue}>
            <TableTitle
              caption={<strong>{tableHeaderFn(column.caption)}</strong>}
              subCaption={replace}
              name={column.name}
            />
          </td>,
        );
      }
    });

    if (total !== 0) {
      const peakCol = tableHeader.find((c: any) => c.name === "peakwavedir");
      if (peakCol) {
        const replace =
          peakCol.output_unit_name === "kts" || peakCol.ud_unit_name === "kt"
            ? "kn"
            : peakCol.ud_unit_name === "None"
              ? " - "
              : peakCol.ud_unit_name;
        headers.push(
          <td key="peakwavedir-hf" style={hBlueL}>
            <TableTitle
              caption={<strong>{tableHeaderFn(peakCol.caption)}</strong>}
              subCaption={replace}
              name={peakCol.name}
            />
          </td>,
        );
      } else {
        headers.push(
          <td key="totalseadir-hf" style={hBlueL}>
            <TableTitle
              caption={<strong>Dir</strong>}
              subCaption=""
              name="totalseadir_virtual"
            />
          </td>,
        );
      }
      ["sigwaveheight", "peakwaveperiod", "maxwave"].forEach((colName) => {
        tableHeader.map((column: any, index: number) => {
          if (column.name === colName) {
            const replace =
              column.output_unit_name === "kts" || column.ud_unit_name === "kt"
                ? "kn"
                : column.ud_unit_name === "None"
                  ? " - "
                  : column.ud_unit_name;
            headers.push(
              <td key={column.name} style={hBlue}>
                <TableTitle
                  caption={<strong>{tableHeaderFn(column.caption)}</strong>}
                  subCaption={replace}
                  name={column.name}
                />
              </td>,
            );
          }
        });
      });
    }

    let firstW = true;
    const weatherColumns = [
      "a_2mtemp",
      "a_2mrelativehumidity",
      "heatindex",
      "cloudbase",
      "modelvisibility",
      "totalcloud",
      "rainrate",
      "totalprecip",
      "mslp",
      "weather_text",
    ];
    weatherColumns.forEach((columnName) => {
      tableHeader.map((column: any, index: number) => {
        if (column.name === columnName) {
          const replace =
            column.output_unit_name === "kts" || column.ud_unit_name === "kt"
              ? "kn"
              : column.ud_unit_name === "None"
                ? " - "
                : column.ud_unit_name;
          headers.push(
            <td key={column.name} style={firstW ? hBlueG : hBlue}>
              <TableTitle
                caption={<strong>{tableHeaderFn(column.caption)}</strong>}
                subCaption={replace}
                name={column.name}
              />
            </td>,
          );
          firstW = false;
        }
      });
    });

    if (currents > 0) {
      tableHeader.forEach((column: any, index: number) => {
        if (
          column.name === "surfacecurrentdirection" ||
          column.name === "surfacecurrentspeed"
        ) {
          const replace =
            column.output_unit_name === "kts" || column.ud_unit_name === "kt"
              ? "kn"
              : column.output_unit_name === "None"
                ? " - "
                : column.ud_unit_name;
          headers.push(
            <td key={column.name} style={hBlue}>
              <TableTitle
                caption={<strong>{tableHeaderFn(column.caption)}</strong>}
                subCaption={replace}
                name={column.name}
              />
            </td>,
          );
        }
      });
    }
    return headers;
  }

  function HeaderUnit() {
    let headers = new Array();

    const datetimeCol = tableHeader.find((c: any) => c.name === "datetimeutc");
    if (datetimeCol)
      headers.push(
        <td key="dt-unit" style={hBlue}>
          <TableUnit caption={tableHeaderFn(datetimeCol.caption)} />
        </td>,
      );

    if (latLand) {
      headers.push(
        <td key="lat-unit" style={hBlue}>
          <TableUnit caption="Lat" />
        </td>,
      );
      headers.push(
        <td key="lon-unit" style={hBlue}>
          <TableUnit caption="Lon" />
        </td>,
      );
    }

    tableHeader.map((column: any, index: number) => {
      if (
        column.name === "a_10mwinddir" ||
        column.name === "a_10mwindspeed" ||
        column.name === "a_10mgust" ||
        column.name === "a_50mwindspeed" ||
        column.name === "a_80mwindspeed" ||
        column.name === "a_100mwindspeed"
      ) {
        headers.push(
          <td key={column.name} style={hBlue}>
            <TableUnit caption={tableHeaderFn(column.caption)} />
          </td>,
        );
      }
    });

    let directionWw = false;
    tableHeader.map((column: any, index: number) => {
      if (column.name === "windseadirection" && windwave > 0) {
        directionWw = true;
        headers.push(
          <td key={column.name} style={hBlueL}>
            <TableUnit caption={tableHeaderFn(column.caption)} />
          </td>,
        );
      }
    });
    if (directionWw === false && windwave > 0) {
      headers.push(
        <td key="ww-dir-unit-fallback" style={hBlueL}>
          <TableUnit caption="Dir" />
        </td>,
      );
    }
    tableHeader.map((column: any, index: number) => {
      if (
        (column.name === "windseaheight" || column.name === "windseaperiod") &&
        windwave > 0
      ) {
        headers.push(
          <td key={column.name} style={hBlue}>
            <TableUnit caption={tableHeaderFn(column.caption)} />
          </td>,
        );
      }
    });

    let directionS1 = false;
    tableHeader.map((column: any, index: number) => {
      if (column.name === "swell1direction") {
        directionS1 = true;
        headers.push(
          <td key={column.name} style={hBlueL}>
            <TableUnit caption={tableHeaderFn(column.caption)} />
          </td>,
        );
      }
    });
    if (directionS1 === false && swell1 !== 0) {
      headers.push(
        <td key="s1-dir-unit-fallback" style={hBlueL}>
          <TableUnit caption="Dir" />
        </td>,
      );
    }
    tableHeader.map((column: any, index: number) => {
      if (column.name === "swell1height" || column.name === "swell1period") {
        headers.push(
          <td key={column.name} style={hBlue}>
            <TableUnit caption={tableHeaderFn(column.caption)} />
          </td>,
        );
      }
    });

    let directionS2 = false;
    tableHeader.map((column: any, index: number) => {
      if (column.name === "swell2direction") {
        directionS2 = true;
        headers.push(
          <td key={column.name} style={hBlueL}>
            <TableUnit caption={tableHeaderFn(column.caption)} />
          </td>,
        );
      }
    });
    if (directionS2 === false && swell2 !== 0) {
      headers.push(
        <td key="s2-dir-unit-fallback" style={hBlueL}>
          <TableUnit caption="Dir" />
        </td>,
      );
    }
    tableHeader.map((column: any, index: number) => {
      if (column.name === "swell2height" || column.name === "swell2period") {
        headers.push(
          <td key={column.name} style={hBlue}>
            <TableUnit caption={tableHeaderFn(column.caption)} />
          </td>,
        );
      }
    });

    if (total !== 0) {
      const peakCol = tableHeader.find((c: any) => c.name === "peakwavedir");
      if (peakCol) {
        headers.push(
          <td key="peakwavedir-unit" style={hBlueL}>
            <TableUnit caption={tableHeaderFn(peakCol.caption)} />
          </td>,
        );
      } else {
        headers.push(
          <td key="totalseadir-unit" style={hBlueL}>
            <TableUnit caption="Dir" />
          </td>,
        );
      }
      ["sigwaveheight", "peakwaveperiod", "maxwave"].forEach((colName) => {
        tableHeader.map((column: any, index: number) => {
          if (column.name === colName) {
            headers.push(
              <td key={column.name} style={hBlue}>
                <TableUnit caption={tableHeaderFn(column.caption)} />
              </td>,
            );
          }
        });
      });
    }

    let firstW = true;
    const weatherColumns = [
      "a_2mtemp",
      "a_2mrelativehumidity",
      "heatindex",
      "cloudbase",
      "modelvisibility",
      "totalcloud",
      "rainrate",
      "totalprecip",
      "mslp",
      "weather_text",
    ];
    weatherColumns.forEach((columnName) => {
      tableHeader.map((column: any, index: number) => {
        if (column.name === columnName) {
          headers.push(
            <td key={column.name} style={firstW ? hBlueG : hBlue}>
              <TableUnit caption={tableHeaderFn(column.caption)} />
            </td>,
          );
          firstW = false;
        }
      });
    });

    if (currents > 0) {
      tableHeader.map((column: any, index: number) => {
        if (
          column.name === "surfacecurrentdirection" ||
          column.name === "surfacecurrentspeed"
        ) {
          headers.push(
            <td key={column.name} style={hBlue}>
              <TableUnit caption={tableHeaderFn(column.caption)} />
            </td>,
          );
        }
      });
    }
    return headers;
  }

  function HeaderRange() {
    let headers = new Array();

    const datetimeCol = tableHeader.find((c: any) => c.name === "datetimeutc");
    if (datetimeCol) {
      headers.push(
        <TableValue
          key="dt-range"
          caption={tableHeaderFn(datetimeCol.caption)}
          name="datetimeutc"
          {...tableValueProps}
        />,
      );
    }

    if (latLand) {
      headers.push(<td key="lat-range" style={hWhite} />);
      headers.push(<td key="lon-range" style={hWhite} />);
    }

    tableHeader.map((column: any, index: number) => {
      if (column.name === "a_10mwinddir") {
        headers.push(
          <td key={column.name} style={hWhite}>
            <TableValue
              caption={tableHeaderFn(column.caption)}
              name={column.name}
              {...tableValueProps}
            />
          </td>,
        );
      }
    });
    tableHeader.map((column: any, index: number) => {
      if (
        column.name === "a_10mwindspeed" ||
        column.name === "a_10mgust" ||
        column.name === "a_50mwindspeed" ||
        column.name === "a_80mwindspeed" ||
        column.name === "a_100mwindspeed"
      ) {
        headers.push(
          <td key={column.name} style={hWhite}>
            <TableValue
              caption={tableHeaderFn(column.caption)}
              name={column.name}
              {...tableValueProps}
            />
          </td>,
        );
      }
    });

    let directionWw = false;
    tableHeader.map((column: any, index: number) => {
      if (column.name === "windseadirection" && windwave > 0) {
        directionWw = true;
        headers.push(
          <td key={column.name} style={hWhite}>
            <TableValue
              caption={tableHeaderFn(column.caption)}
              name={column.name}
              {...tableValueProps}
            />
          </td>,
        );
      }
    });
    if (directionWw === false && windwave > 0) {
      headers.push(
        <td key="ww-dir-range-fallback" style={hWhite}>
          <TableValue
            caption="Dir"
            name="windseadirection"
            {...tableValueProps}
          />
        </td>,
      );
    }
    tableHeader.map((column: any, index: number) => {
      if (
        (column.name === "windseaheight" || column.name === "windseaperiod") &&
        windwave > 0
      ) {
        headers.push(
          <td key={column.name} style={hWhite}>
            <TableValue
              caption={tableHeaderFn(column.caption)}
              name={column.name}
              {...tableValueProps}
            />
          </td>,
        );
      }
    });

    let directionS1 = false;
    tableHeader.map((column: any, index: number) => {
      if (column.name === "swell1direction" && swell1 > 0) {
        directionS1 = true;
        headers.push(
          <td key={column.name} style={hWhite}>
            <TableValue
              caption={tableHeaderFn(column.caption)}
              name={column.name}
              {...tableValueProps}
            />
          </td>,
        );
      }
    });
    if (directionS1 === false && swell1 > 0) {
      headers.push(
        <td key="s1-dir-range-fallback" style={hWhite}>
          <TableValue
            caption="Dir"
            name="swell1direction"
            {...tableValueProps}
          />
        </td>,
      );
    }
    tableHeader.map((column: any, index: number) => {
      if (column.name === "swell1height" || column.name === "swell1period") {
        headers.push(
          <td key={column.name} style={hWhite}>
            <TableValue
              caption={tableHeaderFn(column.caption)}
              name={column.name}
              {...tableValueProps}
            />
          </td>,
        );
      }
    });

    let directionS2 = false;
    tableHeader.map((column: any, index: number) => {
      if (column.name === "swell2direction") {
        directionS2 = true;
        headers.push(
          <td key={column.name} style={hWhite}>
            <TableValue
              caption={tableHeaderFn(column.caption)}
              name={column.name}
              {...tableValueProps}
            />
          </td>,
        );
      }
    });
    if (directionS2 === false && swell2 !== 0) {
      headers.push(
        <td key="s2-dir-range-fallback" style={hWhite}>
          <TableValue
            caption="Dir"
            name="swell2direction"
            {...tableValueProps}
          />
        </td>,
      );
    }
    tableHeader.map((column: any, index: number) => {
      if (column.name === "swell2height" || column.name === "swell2period") {
        headers.push(
          <td key={column.name} style={hWhite}>
            <TableValue
              caption={tableHeaderFn(column.caption)}
              name={column.name}
              {...tableValueProps}
            />
          </td>,
        );
      }
    });

    if (total !== 0) {
      const peakCol = tableHeader.find((c: any) => c.name === "peakwavedir");
      if (peakCol) {
        headers.push(
          <td key="peakwavedir-range" style={hWhite}>
            <TableValue
              caption={tableHeaderFn(peakCol.caption)}
              name={peakCol.name}
              {...tableValueProps}
            />
          </td>,
        );
      } else {
        headers.push(
          <td key="totalseadir-range" style={hWhite}>
            <TableValue
              caption="Dir"
              name="totalseadir_virtual"
              {...tableValueProps}
            />
          </td>,
        );
      }
      ["sigwaveheight", "peakwaveperiod", "maxwave"].forEach((colName) => {
        tableHeader.map((column: any, index: number) => {
          if (column.name === colName) {
            headers.push(
              <td key={column.name} style={hWhite}>
                <TableValue
                  caption={tableHeaderFn(column.caption)}
                  name={column.name}
                  {...tableValueProps}
                />
              </td>,
            );
          }
        });
      });
    }

    const weatherColumns = [
      "a_2mtemp",
      "a_2mrelativehumidity",
      "heatindex",
      "cloudbase",
      "modelvisibility",
      "totalcloud",
      "rainrate",
      "totalprecip",
      "mslp",
      "weather_text",
    ];
    weatherColumns.forEach((columnName) => {
      tableHeader.map((column: any, index: number) => {
        if (column.name === columnName) {
          headers.push(
            <td key={column.name} style={hWhite}>
              <TableValue
                caption={tableHeaderFn(column.caption)}
                name={column.name}
                {...tableValueProps}
              />
            </td>,
          );
        }
      });
    });

    tableHeader.map((column: any, index: number) => {
      if (
        column.name === "surfacecurrentdirection" ||
        column.name === "surfacecurrentspeed"
      ) {
        headers.push(
          <td key={column.name} style={hWhite}>
            <TableValue
              caption={tableHeaderFn(column.caption)}
              name={column.name}
              {...tableValueProps}
            />
          </td>,
        );
      }
    });

    return headers;
  }

return (
    <div className={open ? "sideNavOpen" : "sideNavClose"}>
      <Box className="fug-container own-fug-container bg-default flex sidenav-full">
        <div className="content-wrap page-forecast">
          <div className="discussion-content" style={{ marginBottom: "-20", marginTop: "-16px", padding: "20px" }}>
            {!noDataMessage && (
              <Table className="tabledis" style={{ background: "white", borderRadius: "20px", boxShadow: "2px, 2px, 2px black", height: "10vh" }}>
                <TableRow>
                  <td>
                    <strong>To:</strong> {""}
                    {localStorage.getItem("project") ? localStorage.getItem("project")?.split("-")[0] : " - "}
                  </td>
                </TableRow>
                <TableRow>
                  <td>
                    <strong>Subject:</strong>{" "}
                    {subject.vessel_rig_platform_name && subject.field_area_name
                      ? validity_b?.latitude && validity_b?.longitude
                        ? `Weather Forecast for ${subject.vessel_rig_platform_name} at ${Math.abs(validity_b.latitude)}°${validity_b.latitude >= 0 ? "N" : "S"} ${Math.abs(validity_b.longitude)}°${validity_b.longitude >= 0 ? "E" : "W"}`
                        : `Weather Forecast for ${subject.vessel_rig_platform_name} valid for ${subject.field_area_name}`
                      : "NIL"}
                  </td>
                </TableRow>
                <TableRow>
                  <td>
                    <strong>Validity:</strong>{" "}
                    {validity_a && validity_a.length > 0 && validity_b
                      ? `Forecast valid ${validity_a.reduce((sum: number, v: { time_block: number }) => sum + v.time_block, 0)} hours from ${new Date(validity_b.issue_date_time).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false })} ${new Date(validity_b.issue_date_time).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" })} ${forecastDatas?.[0]?.[0]?.time_zone?.split(")")[0] ?? ""})`
                      : "NIL"}
                  </td>
                </TableRow>
                <TableRow>
                  <td>
                    <strong style={{ color: discussion.warning && discussion.warning.trim() !== "" ? "red" : "black" }}>Warning:</strong>{" "}
                    {discussion.warning && discussion.warning.trim() !== "" ? (
                      <span style={{ color: "red" }}>{discussion.warning}</span>
                    ) : (
                      <span style={{ color: "black" }}>NIL</span>
                    )}
                  </td>
                </TableRow>
                <TableRow>
                  <td><strong>Met Situation:</strong>{" "}{discussion.synopsis ? discussion.synopsis : " NIL "}</td>
                </TableRow>
                <TableRow>
                  <td><strong>Tropical Advisory:</strong>{" "}{discussion.advisory ? discussion.advisory : "NIL"}</td>
                </TableRow>
                <TableRow>
                  <td style={{ width: "50%" }}>
                    <strong>Weather:</strong>{" "}
                    {discussion.notes !== undefined || discussion.notes ? discussion.notes : "NIL"}
                  </td>
                </TableRow>
              </Table>
            )}
          </div>
          <Main open={open} className={"main"} style={{ overflow: "hidden", marginTop: "-32px", height }}>            {loading ? (
            <div className="loader-div"><WeatherLoader /></div>
          ) : noDataMessage ? (
            <div className="no-data-wrapper">
              <h1 className="noData">{noDataMessage}</h1>
            </div>

          ) : (
            <div className={"container-section"}>
              <div className={"forecast-div"}>
                <h6 className={"forcast-header"}>Time-Step</h6>
                <FormControl sx={{ m: 1, minWidth: "auto", maxWidth: 500 }} size="small">
                  <Select
                    value={maxMode}
                    onChange={(e) => { const val = e.target.value; setMaxMode(val === "ALL" ? "ALL" : (Number(val) as any)); }}
                    style={{ backgroundColor: "white", borderRadius: "10px", textAlign: "center", fontSize: 14 }}
                  >
                    <MenuItem value="ALL">All</MenuItem>
                    <MenuItem value={3}>Max 3 hour Period</MenuItem>
                    <MenuItem value={6}>Max 6 hour Period</MenuItem>
                    <MenuItem value={12}>Max 12 hour Period</MenuItem>
                    <MenuItem value={24}>Max 24 hour Period</MenuItem>
                  </Select>
                </FormControl>

                <h6 className={"forcast-header"}>Weather Windows</h6>
                <FormControl sx={{ m: 1, minWidth: "auto", maxWidth: 500, flex: 1 }} size="small">
                  <Select
                    labelId="model-data"
                    id="model-data"
                    value={criteriaDatas.length === 0 ? "None Defined" : SelectValue}
                    onChange={(s) => { if (s.target.value === "None Defined") return; setSelectValue(s.target.value); }}
                    className="menuitem"
                    style={{ backgroundColor: "white", borderRadius: "10px", textAlign: "center", fontSize: 15 }}
                    MenuProps={{ PaperProps: { style: { maxWidth: "none" } } }}
                  >
                    {criteriaDatas.length === 0 ? (
                      <MenuItem className="menuitem" value="None Defined">None Defined</MenuItem>
                    ) : (
                      criteriaDatas.map((data: any) => (
                        <MenuItem className="menuitem" key={data.forecast_osf_criteria_id} value={data.forecast_osf_criteria_id} style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                          {data.criteria_name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>

                <div className="download-section">
                  <div className="download-label">Download</div>
                  <div className="download-buttons">
                    <Button variant="contained" onClick={handleDownloadCsv} style={{ backgroundColor: "#4CAF50", color: "white", borderRadius: "10px" }} startIcon={<InsertDriveFileIcon />} disabled={loadingCsv}>
                      {loadingCsv ? <CircularProgress size={20} style={{ color: "white" }} /> : "CSV"}
                    </Button>
                    <Button variant="contained" onClick={handleDownload} style={{ backgroundColor: "red", color: "white", borderRadius: "10px" }} startIcon={<PictureAsPdfIcon />} disabled={loadingPdf}>
                      {loadingPdf ? <CircularProgress size={20} style={{ color: "white" }} /> : "PDF"}
                    </Button>
                  </div>
                </div>
              </div>
              <Paper sx={{ width: "100%", overflow: "visible", "@media (max-width: 960px)": { width: "100%", maxWidth: "100vw", }, }}>                <table style={{ minWidth: "100%" }}>
                <thead style={{ position: "sticky", top: 0, border: "0.5px solid #437c92", backgroundColor: "#add8e6" }}>
                  <tr style={{ border: "0.5px solid #437c92" }}>
                    {showWxWindow ? (
                      <th className="theadtitleWhite" rowSpan={4}>
                        <td style={{ background: "white", color: "#192b3c", textAlign: "center", width: "95%", display: "flex", justifyContent: "center" }} className={"wx-window"} rowSpan={4}>
                          <p><strong>WX Window</strong></p>
                        </td>
                      </th>
                    ) : null}
                    {HeaderRange().map((d: any, i: number) => <th key={i} className="theadtitleWhite">{d}</th>)}
                  </tr>
                  <tr className="colorRow">
                    <th className="theadtitle"><p><strong>LocalTime</strong></p></th>
                    {latLand ? (
                      <>
                        <th className="theadtitle"><p><strong>Lat</strong></p></th>
                        <th className="theadtitle"><p><strong>Lon</strong></p></th>
                      </>
                    ) : null}
                    <th className="theadtitle" colSpan={wind}><p><strong>Winds</strong></p></th>
                    <th className="theadtitle" colSpan={isDirWw === true ? windwave + 1 : windwave + 1}><p><strong>Wind Waves</strong></p></th>
                    {swell1 > 0 ? <th className="theadtitle" colSpan={presentS1 === true ? swell1 + 1 : swell1}><p><strong>Swell 1</strong></p></th> : <></>}
                    {swell2 !== 0 ? <th className="threadtitle" colSpan={presentS2 === true ? swell2 + 1 : swell2}><p style={{ fontWeight: "normal" }}><strong>Swell 2</strong></p></th> : null}
                    {total > 0 ? (
                      <th className="theadtitle" colSpan={tableHeader.find((c: any) => c.name === "peakwavedir") ? total + 1 : total + 1}>
                        <p><strong>Total Sea</strong></p>
                      </th>
                    ) : <></>}
                    {(() => {
                      const weatherColumns = ["a_2mtemp", "a_2mrelativehumidity", "heatindex", "cloudbase", "modelvisibility", "totalcloud", "rainrate", "totalprecip", "mslp", "weather_text"];
                      const dynamicWeatherCount = weatherColumns.filter(col => tableHeader.some((h: any) => h.name === col)).length;
                      return dynamicWeatherCount > 0 ? <th className="theadtitle" colSpan={dynamicWeatherCount}><p><strong>Weather</strong></p></th> : null;
                    })()}
                    {currents !== 0 && <th className="theadtitle" colSpan={currents}><p><strong>Currents</strong></p></th>}
                  </tr>
                <tr className="colorRow">
                    {HeaderFunction().map((d: any, i: number) => (
                      <th key={i} className="theadtitle" style={{ padding: 0, fontWeight: "normal", border: "0.5px solid #437c92" }}>
                        {d}
                      </th>
                    ))}
                  </tr>

                  <tr>
                    {HeaderUnit().map((d: any, i: number) => (
                      <th key={i} className="theadtitle" style={{ padding: 0, fontWeight: "normal", border: "0.5px solid #437c92" }}>
                        {d}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>{tableContent}</tbody>
              </table>
              </Paper>
            </div>
          )}
          </Main>
        </div>
      </Box>
    </div>
  );
};
export default ForeCast;
